import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  TsBadgeModule,
  TsButtonModule,
  TSIconModule,
  TsInputComponent,
  TsPageHeaderModule,
  TsPaginatorChangeEvent,
  TsPaginatorModule,
} from 'tsui';
import { TsTableComponent } from 'tsui/lib/ts-table';

import { ColumnDef, FieldDefType } from 'tsui/lib/ts-table/ts-table.model';

import { filter } from 'rxjs';

import { ReportPeriod } from '../../models/report-period';
import { ReportPeriodUtils } from '../../utils/report-period.utils';
import { MatMenuModule } from '@angular/material/menu';
import { PaginatedGeneralLedger, PaginatedTrialBalance } from '../../state/asset-management.state';
import { FormControl } from '@angular/forms';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementFileSources } from '../../models';
import { GraphqlFilterSort } from '@shared/models/graphql';
import { GridApi, SortChangedEvent } from 'ag-grid-enterprise';
import { ReportPeriodSelectorComponent } from '../report-period-selector/report-period-selector.component';

export type AccountingReportColumns<T = any> = {
  [columnName in keyof Partial<T>]: {
    label: string;
    type: FieldDefType;
    minWidth?: number;
  };
};

export interface AccountingReportConfig {
  title: string;
  columns: AccountingReportColumns;
  enableImport: boolean;
  enableExport: boolean;
}

@Component({
  selector: 'accounting-report-grid',
  standalone: true,
  imports: [
    TSIconModule,
    TsBadgeModule,
    TsButtonModule,
    TsInputComponent,
    TsPageHeaderModule,
    TsPaginatorModule,
    TsTableComponent,
    MatMenuModule,
    TitleCasePipe,
    NgTemplateOutlet,
    ReportPeriodSelectorComponent,
  ],
  templateUrl: './accounting-report-grid.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountingReportGridComponent implements OnInit {
  @Input() importLoading: boolean;
  @Input() accountingReportLoading: boolean;

  @Input({ required: true }) set accountingReportPage(val: PaginatedTrialBalance | PaginatedGeneralLedger) {
    this._accountingReportPage = val;

    if (this.accountFilterSearchEnabled) {
      this.filterAccountsForm.setValue(this._accountingReportPage.filter.q, { emitEvent: false });
    }

    this.gridApi?.resetColumnState();
  }
  @Input({ required: true }) reportPeriod: ReportPeriod;

  @Input({ required: true }) set config({ title, enableImport, enableExport, columns }: AccountingReportConfig) {
    this.title = title;
    this.enableImport = enableImport;
    this.enableExport = enableExport;
    this.columnDefs = Object.entries(columns).map(([k, v]) => ({
      label: v.label,
      // hack: some nested properties separated by the dot notation are changed on ts-table.service.ts
      // the service assumes that whenever we receive a field name with a dot, it is a nested FieldDef
      // that's not ideal for use cases like this one, where we want to use the dot notation to access nested properties.
      // e.g.: 'gl_account.gl_account_number' => 'gl_account>gl_account_number'
      name: k.split('.').join('>'),
      valueGetter: (params: any) => {
        if (k.includes('.')) {
          const keys = k.split('.');
          return params.data[keys[0]][keys[1]];
        }
        return params.data[k];
      },
      type: v.type as FieldDefType,
      ...AccountingReportGridComponent.commonColumnDefParams(k, v.label, (v as any)?.minWidth),
    }));
  }

  @Output() exportAccountingReport = new EventEmitter<void>();
  @Output() importAccountingReport = new EventEmitter<{ file: File; source: AssetManagementFileSources }>();
  @Output() pageChanged = new EventEmitter<{ page: number; per_page: number }>();
  @Output() reportPeriodChanged = new EventEmitter<ReportPeriod>();
  @Output() accountFilterChanged = new EventEmitter<string>();
  @Output() sortChanged = new EventEmitter<GraphqlFilterSort[]>();

  readonly getReportPeriodBadgeLabel = ReportPeriodUtils.getReportPeriodBadgeLabel;
  readonly filterAccountsForm = new FormControl();

  title: string;
  enableImport: boolean;
  enableExport: boolean;
  columnDefs: ColumnDef[];
  gridApi: GridApi;
  _accountingReportPage: PaginatedTrialBalance | PaginatedGeneralLedger;

  get accountFilterSearchEnabled() {
    return typeof this._accountingReportPage?.filter?.q === 'string';
  }

  ngOnInit() {
    this.filterAccountsForm.valueChanges
      .pipe(
        TsRxjsOperators.memoizedDebounceTime(500),
        filter((val) => val?.length > 1 || val?.length === 0),
      )
      .subscribe((value) => {
        this.accountFilterChanged.emit(value);
      });
  }

  onPageChanged({ activePage, pageSize }: TsPaginatorChangeEvent) {
    this.pageChanged.emit({
      page: activePage,
      per_page: pageSize,
    });
  }

  onSortChanged({ columns }: SortChangedEvent) {
    const field = (<any>columns[0])?.colId;
    const order = (<any>columns[0])?.sort;
    const sort = order ? [{ field, order }] : [];

    if (field) {
      this.sortChanged.emit(sort);
      return;
    }

    console.error('Invalid sort event');
  }

  static commonColumnDefParams(field: string, headerName: string, minWidth?: number, flex = 1) {
    return {
      meta: { readonly: true, scale: 2 },
      tsTableOptions: {
        field,
        sortable: true,
        editable: false,
        flex,
        ...(minWidth ? { minWidth } : {}),
        headerName,
      },
    };
  }

  onGridReady($event: any) {
    this.gridApi = $event.api;
  }
}
